import { createReducer } from 'typesafe-actions'
import { User } from 'oidc-client-ts'
import { AvpContextClaims, Claims } from '@org-avp/context-claims'

import {
    loginAsync,
    loginStart,
    resetGrpAuthState,
    setIsLoading,
    setShouldConnect,
    setShouldConnectFromLoginComponent,
    logout,
    updateUserAfterRefresh,
    e3dReinitBumped,
    setSelectedDealerId,
} from '../actions/app/e3DGrpAuth.actions'
import { getPersistedE3DGrpAuth } from '../persistLocalStorageE3DAuthState'
import { logger } from '../../utils/dccLogger'

export type E3DGrpAuthState = {
    user: User | null
    shouldConnect: boolean
    shouldConnectFromLoginComponent: boolean
    loading: boolean
    isLoggedIn: boolean
    error: string | null
    reinitCounter: number
    validDealerIds: string[]
    selectedDealerId: string | null
}

const defaultInitialState: E3DGrpAuthState = {
    user: null,
    shouldConnect: false,
    shouldConnectFromLoginComponent: false,
    loading: false,
    isLoggedIn: false,
    error: null,
    reinitCounter: 0,
    validDealerIds: [],
    selectedDealerId: null,
}

// KVPS format: 3-letter market, 5 alphanumerics, brand letter (same filter 3DE applies)
const DEALER_ID_PATTERN = /^[A-Z]{3}[A-Z0-9]{5}[AVCELMNPS]$/

export const getDealerIdsFromUser = (user: User | null): string[] => {
    if (!user?.profile) {
        return []
    }
    try {
        return AvpContextClaims.getDealerIds(user.profile as Claims)
            .filter((dealerId) => DEALER_ID_PATTERN.test(dealerId))
            .sort()
    } catch (error) {
        logger.error(`Error reading dealer IDs from GRP token: ${error}`)
        return []
    }
}

// Keeps the selection only while it is still in the token; a single dealer ID is auto-selected.
export const withDealerSelection = (state: E3DGrpAuthState): E3DGrpAuthState => {
    const validDealerIds = getDealerIdsFromUser(state.user)
    let selectedDealerId: string | null = null
    if (validDealerIds.length === 1) {
        [selectedDealerId] = validDealerIds
    } else if (state.selectedDealerId && validDealerIds.includes(state.selectedDealerId)) {
        selectedDealerId = state.selectedDealerId
    }
    return { ...state, validDealerIds, selectedDealerId }
}

const persistedState = getPersistedE3DGrpAuth()
const initialState: E3DGrpAuthState = persistedState
    ? withDealerSelection({ ...defaultInitialState, ...persistedState })
    : defaultInitialState

export const e3DGrpAuthReducer = createReducer(initialState)
    .handleAction(loginStart, (state) => ({
        ...state,
    }))
    .handleAction(loginAsync.request, (state) => {
        return {
            ...state,
            loading: true,
            error: null,
        }
    })
    .handleAction(loginAsync.success, (state, action) => withDealerSelection({
        ...state,
        user: action.payload,
        loading: false,
        isLoggedIn: true,
        error: null,
    }))
    .handleAction(loginAsync.failure, (state, action) => {
        return {
            ...state,
            user: null,
            loading: false,
            isLoggedIn: false,
            error: action.payload,
            validDealerIds: [],
            selectedDealerId: null,
        }
    })
    .handleAction(setIsLoading, (state, action) => ({
        ...state,
        loading: action.payload,
    }))
    .handleAction(logout, (state) => {
        return {
            ...state,
            user: null,
            isLoggedIn: false,
            loading: false,
            error: null,
            validDealerIds: [],
            selectedDealerId: null,
        }
    })
    .handleAction(setSelectedDealerId, (state, action) => ({
        ...state,
        selectedDealerId: state.validDealerIds.includes(action.payload) ? action.payload : state.selectedDealerId,
    }))
    .handleAction(setShouldConnect, (state, action) => ({
        ...state,
        shouldConnect: action.payload,
    }))
    .handleAction(setShouldConnectFromLoginComponent, (state, action) => ({
        ...state,
        shouldConnectFromLoginComponent: action.payload,
    }))
    .handleAction(updateUserAfterRefresh, (state, action) => withDealerSelection({
        ...state,
        user: action.payload,
    }))
    .handleAction(resetGrpAuthState, () => defaultInitialState)
    .handleAction(e3dReinitBumped, (state, action) => ({
        ...state,
        reinitCounter: action.payload,
    }))

export default e3DGrpAuthReducer
